import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarCheck2, Check, X, Save, ChevronLeft, ChevronRight, Users, TrendingUp, Trash2, StickyNote } from "lucide-react";

/**
 * Attendance page — monthly calendar navigator + roll-call per session
 * + per-player attendance rate ranking.
 */
export default function AttendancePage() {
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [openDate, setOpenDate] = useState(null);
  const [roll, setRoll] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [exportRange, setExportRange] = useState("year");

  useEffect(() => {
    (async () => {
      try {
        const [t, p] = await Promise.all([api.get("/teams"), api.get("/players")]);
        const tl = Array.isArray(t.data) ? t.data : [];
        setTeams(tl); setPlayers(Array.isArray(p.data) ? p.data : []);
        if (!teamId && tl[0]) setTeamId(tl[0].id);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, []);

  useEffect(() => {
    if (!teamId) return;
    (async () => {
      try {
        const [s, st] = await Promise.all([
          api.get(`/attendance?team_id=${teamId}&month=${month}`),
          api.get(`/attendance/team/${teamId}/stats`),
        ]);
        setSessions(Array.isArray(s.data) ? s.data : []);
        setStats(st.data && typeof st.data === "object" ? st.data : null);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, [teamId, month]);

  const teamPlayers = useMemo(() => players.filter(p => p.team_id === teamId), [players, teamId]);
  const asList = (v) => (Array.isArray(v) ? v : []);
  const sessionByDate = useMemo(() => Object.fromEntries(sessions.map(s => [String(s.date || "").slice(0, 10), s])), [sessions]);

  const daysGrid = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = new Date(Date.UTC(y, m - 1, 1));
    const startOffset = (first.getUTCDay() + 6) % 7;
    const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const grid = [];
    for (let i = 0; i < startOffset; i++) grid.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      grid.push({ day: d, iso, weekday: (startOffset + d - 1) % 7, session: sessionByDate[iso] });
    }
    while (grid.length % 7 !== 0) grid.push(null);
    return grid;
  }, [month, sessionByDate]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    setMonth(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
  };

  const openDay = (dayIso) => {
    setOpenDate(dayIso);
    const existing = sessionByDate[dayIso];
    if (existing) {
      const r = {};
      (asList(existing.attendance)).forEach(a => { if (a && a.player_id) r[a.player_id] = { present: !!a.present, note: a.note || "" }; });
      teamPlayers.forEach(p => { if (!(p.id in r)) r[p.id] = { present: false, note: "" }; });
      setRoll(r);
      setNotes(existing.notes || "");
    } else {
      const r = {};
      teamPlayers.forEach(p => { r[p.id] = { present: true, note: "" }; });
      setRoll(r);
      setNotes("");
    }
  };

  const togglePresent = (pid) => setRoll(r => ({ ...r, [pid]: { ...(r[pid] || { present: true, note: "" }), present: !(r[pid]?.present) } }));
  const setNoteFor = (pid, val) => setRoll(r => ({ ...r, [pid]: { ...(r[pid] || { present: true, note: "" }), note: val } }));

  const markAll = (val) => {
    setRoll(r => {
      const next = { ...r };
      teamPlayers.forEach(p => { next[p.id] = { present: val, note: next[p.id]?.note || "" }; });
      return next;
    });
  };

  const refresh = async () => {
    const [s, st] = await Promise.all([
      api.get(`/attendance?team_id=${teamId}&month=${month}`),
      api.get(`/attendance/team/${teamId}/stats`),
    ]);
    setSessions(s.data); setStats(st.data);
  };

  const saveRoll = async () => {
    if (!openDate) return;
    setSaving(true);
    try {
      const payload = {
        team_id: teamId,
        date: openDate,
        attendance: teamPlayers.map(p => ({
          player_id: p.id,
          present: !!roll[p.id]?.present,
          note: roll[p.id]?.note || null,
        })),
        notes: notes || null,
      };
      await api.post("/attendance", payload);
      toast.success("Asistencia guardada");
      await refresh();
      setOpenDate(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const deleteSession = async (id) => {
    if (!window.confirm("¿Eliminar esta sesión?")) return;
    try {
      await api.delete(`/attendance/${id}`);
      toast.success("Sesión eliminada");
      await refresh();
      setOpenDate(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const monthLabel = new Date(month + "-01").toLocaleDateString("es-ES", { month: "long", year: "numeric" });
  const currentTeam = teams.find(t => t.id === teamId);

  return (
    <div>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline flex items-center gap-2"><CalendarCheck2 size={12} /> Asistencia</div>
          <h1 className="font-heading font-black text-5xl tracking-tighter uppercase mt-2">Pasar Lista</h1>
          <p className="text-muted-foreground text-sm mt-2">Registra la asistencia a entrenamientos por sesión. Toca un día para pasar lista.</p>
        </div>
        <select data-testid="att-team" value={teamId} onChange={(e) => setTeamId(e.target.value)} className="h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      <div className="border border-border mb-6">
        <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline">{currentTeam?.name || "—"}</div>
            <div className="font-heading font-black text-2xl uppercase tracking-tight mt-1 capitalize">{monthLabel}</div>
            <div className="text-[10px] font-mono text-muted-foreground mt-1">
              {sessions.length} sesión{sessions.length === 1 ? "" : "es"} este mes · {stats?.total_sessions || 0} totales del equipo
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select data-testid="att-export-range" value={exportRange} onChange={(e) => setExportRange(e.target.value)}
                    className="h-9 bg-card border border-border px-2 font-mono text-[10px] uppercase focus:border-primary focus:outline-none">
              <option value="month">Mes actual</option>
              <option value="quarter">Trimestre</option>
              <option value="year">Año completo</option>
            </select>
            <button data-testid="att-export-xlsx" onClick={async () => {
              if (!teamId) { toast.error("Selecciona un equipo primero"); return; }
              const [y, m] = month.split("-").map(Number);
              let mf = 1, mt = 12, label = `${y}`;
              if (exportRange === "month") { mf = m; mt = m; label = `${y}-${String(m).padStart(2, "0")}`; }
              else if (exportRange === "quarter") { const q = Math.floor((m - 1) / 3); mf = q * 3 + 1; mt = q * 3 + 3; label = `${y}_T${q + 1}`; }
              try {
                const token = localStorage.getItem("access_token");
                const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/attendance/team/${teamId}/export.xlsx?year=${y}&month_from=${mf}&month_to=${mt}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!resp.ok) { toast.error("No se pudo generar el Excel"); return; }
                const blob = await resp.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = `Asistencias_${currentTeam?.name || "equipo"}_${label}.xlsx`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success(`Excel de asistencias (${label}) descargado`);
              } catch { toast.error("Error de red"); }
            }} className="h-9 px-3 border border-primary text-primary hover:bg-primary hover:text-white text-[10px] font-heading uppercase tracking-widest transition-colors">
              📊 Exportar Excel
            </button>
            <button data-testid="att-prev-month" onClick={() => shiftMonth(-1)} className="h-9 w-9 border border-border hover:border-primary flex items-center justify-center"><ChevronLeft size={15} /></button>
            <button data-testid="att-today" onClick={() => setMonth(new Date().toISOString().slice(0, 7))} className="h-9 px-3 border border-border hover:border-primary text-[10px] font-heading uppercase tracking-widest">Hoy</button>
            <button data-testid="att-next-month" onClick={() => shiftMonth(1)} className="h-9 w-9 border border-border hover:border-primary flex items-center justify-center"><ChevronRight size={15} /></button>
          </div>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-7 gap-1 text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-2">
            {["L", "M", "X", "J", "V", "S", "D"].map(d => <div key={d} className="text-center py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="att-grid">
            {daysGrid.map((cell, i) => {
              if (!cell) return <div key={i} className="aspect-square" />;
              const s = cell.session;
              const total = s ? asList(s.attendance).length : 0;
              const present = s ? asList(s.attendance).filter(a => a && a.present).length : 0;
              const isToday = cell.iso === new Date().toISOString().slice(0, 10);
              const scheduled = !s && asList(currentTeam?.training_schedule).some(sl => sl && sl.weekday === cell.weekday);
              return (
                <button
                  key={i}
                  type="button"
                  data-testid={`att-day-${cell.iso}`}
                  onClick={() => openDay(cell.iso)}
                  style={scheduled ? { borderColor: currentTeam?.color || "#dc2626", borderStyle: "dashed" } : undefined}
                  className={`aspect-square border p-1 flex flex-col items-center justify-between text-xs transition-colors ${s ? "border-primary bg-primary/5 hover:bg-primary/10" : "border-border hover:border-primary/50"} ${isToday ? "ring-1 ring-primary" : ""}`}
                >
                  <span className={`font-mono ${isToday ? "text-primary font-bold" : ""}`}>{cell.day}</span>
                  {s && <span className="text-[9px] font-heading text-primary">{present}/{total}</span>}
                  {scheduled && <span className="text-[8px] font-mono" style={{ color: currentTeam?.color || "#dc2626" }}>entreno</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {stats && asList(stats.ranking).length > 0 && (
        <div className="border border-border">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <TrendingUp size={13} className="text-primary" />
            <span className="overline">Ranking de asistencia · {stats.total_sessions} sesiones</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2">
            {asList(stats.ranking).map((r, idx) => (
              <Link key={r.player_id} to={`/player/${r.player_id}`} data-testid={`att-rank-${r.player_id}`} className="p-3 border-b border-r border-border/40 flex items-center gap-3 hover:bg-white/[0.02]">
                <div className="w-7 h-7 border border-primary/50 flex items-center justify-center font-mono text-[10px]">{idx + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-sm uppercase truncate">{r.player_name} {r.jersey_number ? <span className="opacity-60">#{r.jersey_number}</span> : null}</div>
                  <div className="h-1 mt-1 bg-border"><div className={`h-full ${r.attendance_rate >= 80 ? "bg-emerald-500" : r.attendance_rate >= 50 ? "bg-amber-400" : "bg-destructive"}`} style={{ width: `${r.attendance_rate}%` }} /></div>
                </div>
                <div className="text-right">
                  <div className="metric-number text-lg">{r.attendance_rate}%</div>
                  <div className="text-[9px] font-mono text-muted-foreground">{r.present}/{r.counted}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {openDate && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="roll-call-modal">
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">Pasar lista</div>
                <div className="font-heading font-black text-2xl uppercase tracking-tight mt-1">{new Date(openDate + "T00:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}</div>
                <div className="text-[11px] font-mono text-muted-foreground mt-1">{currentTeam?.name}</div>
              </div>
              <button onClick={() => setOpenDate(null)} data-testid="close-roll-btn" className="p-1 hover:text-primary"><X size={18} /></button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" data-testid="mark-all-present" onClick={() => markAll(true)} className="h-8 px-3 border border-emerald-500/60 text-emerald-400 hover:bg-emerald-500/10 text-[10px] uppercase font-heading tracking-widest">Todos presentes</button>
              <button type="button" data-testid="mark-all-absent" onClick={() => markAll(false)} className="h-8 px-3 border border-destructive/60 text-destructive hover:bg-destructive/10 text-[10px] uppercase font-heading tracking-widest">Todos ausentes</button>
              <span className="text-[10px] font-mono text-muted-foreground ml-auto">
                <Users size={11} className="inline mb-0.5" /> {teamPlayers.length} jugadores
              </span>
            </div>
            <div className="border border-border divide-y divide-border/40" data-testid="roll-list">
              {teamPlayers.map(p => {
                const r = roll[p.id] || { present: true, note: "" };
                return (
                  <div key={p.id} data-testid={`roll-row-${p.id}`} className={`p-3 flex items-center gap-3 ${r.present ? "" : "bg-destructive/5"}`}>
                    <button type="button" data-testid={`roll-toggle-${p.id}`} onClick={() => togglePresent(p.id)} className={`w-9 h-9 border-2 flex items-center justify-center transition-colors ${r.present ? "border-emerald-500 bg-emerald-500/20 text-emerald-400" : "border-destructive/50 hover:border-destructive text-destructive"}`}>
                      {r.present ? <Check size={16} /> : <X size={16} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-bold text-sm uppercase truncate">{p.name} {p.jersey_number ? <span className="opacity-60">#{p.jersey_number}</span> : null}</div>
                      <input type="text" data-testid={`roll-note-${p.id}`} value={r.note} onChange={(e) => setNoteFor(p.id, e.target.value)} placeholder={r.present ? "Nota (opcional)" : "Motivo ausencia (opcional)"} className="w-full mt-1 h-7 bg-transparent border-b border-border/60 px-0 font-mono text-xs focus:border-primary focus:outline-none" />
                    </div>
                  </div>
                );
              })}
              {teamPlayers.length === 0 && <div className="p-6 text-center text-muted-foreground text-sm">No hay jugadores en este equipo.</div>}
            </div>
            <div>
              <label className="overline block mb-1"><StickyNote size={11} className="inline mb-0.5" /> Notas de la sesión</label>
              <textarea data-testid="roll-session-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full bg-transparent border border-border p-3 font-mono text-sm focus:border-primary focus:outline-none" />
            </div>
            <div className="flex items-center justify-between pt-2 flex-wrap gap-3">
              {sessionByDate[openDate] && (
                <button type="button" data-testid="del-session-btn" onClick={() => deleteSession(sessionByDate[openDate].id)} className="h-10 px-4 border border-destructive/60 text-destructive hover:bg-destructive/10 text-xs uppercase font-heading tracking-widest flex items-center gap-2">
                  <Trash2 size={13} /> Borrar sesión
                </button>
              )}
              <div className="flex items-center gap-3 ml-auto">
                <button type="button" onClick={() => setOpenDate(null)} className="h-10 px-5 border border-border text-sm uppercase font-heading tracking-wider">Cancelar</button>
                <button type="button" data-testid="save-roll-btn" onClick={saveRoll} disabled={saving} className="btn-volt disabled:opacity-50">
                  <Save size={14} /> {saving ? "Guardando…" : "Guardar asistencia"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
